import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../ui/Button';
import { Link } from 'react-router';
import axiosInstance from '../../utils/axiosInstance';
import config from '../../utils/config';
import {
  missingFieldLabel,
  routeLabel,
  routeReasonLabel,
  sourceLabel,
  stateLabel,
} from '../../utils/equipmentQuality';
import type { EquipmentInventoryResponse, EquipmentInventoryRow } from '../../types';

/**
 * The equipment inventory for the one device the operator has open.
 *
 * The Equipment Inventory page answers "which devices are missing what". This
 * answers the question an operator asks with a device already in front of
 * them: what does this device know, where did each fact come from, and what
 * happens next. Same endpoint, same server-computed wording — the only
 * difference is that it asks for one device.
 *
 * The evidence is printed in full and wraps. It is the only place a
 * manufacturer document is cited, and a citation cut off at the column edge
 * is no citation at all.
 */

/** One row's value, with its unit where the unit means anything. */
const valueLabel = (r: EquipmentInventoryRow): string => {
  if (!r.value) return 'unknown';
  return r.unit && r.unit !== 'photos' ? `${r.value} ${r.unit}` : r.value;
};

const Dot: React.FC = () => (
  <span aria-hidden="true" className="mx-1 text-gray-300">
    ·
  </span>
);

const InventoryRowItem: React.FC<{ row: EquipmentInventoryRow }> = ({ row }) => (
  <li className="py-2">
    <div className="flex items-baseline justify-between gap-4">
      <span className="font-medium">{missingFieldLabel(row.field)}</span>
      <span className="text-right wrap-break-word">{valueLabel(row)}</span>
    </div>
    <span className="mt-0.5 block text-xs text-muted-foreground">
      <span>{stateLabel(row.state)}</span>
      <Dot />
      <span>{sourceLabel(row.source)}</span>
      <Dot />
      <span>{routeLabel(row.route)}</span>
      <Dot />
      <span>{routeReasonLabel(row.route_reason)}</span>
    </span>
    {row.evidence && (
      <span className="mt-0.5 block text-xs text-muted-foreground whitespace-pre-wrap wrap-break-word">
        {row.evidence}
      </span>
    )}
  </li>
);

const EquipmentInventorySection: React.FC<{ deviceId: number }> = ({ deviceId }) => {
  const [rows, setRows] = useState<EquipmentInventoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  // The same three answers the inventory page distinguishes: not allowed,
  // everything else, and an empty result — which is not a failure at all.
  const [error, setError] = useState<'forbidden' | 'failed' | null>(null);

  /**
   * False once this section is gone. An operator opens a device and moves on
   * before the inventory comes back often enough that it is the normal case,
   * and the response then has nothing to render into: writing state onto an
   * unmounted component is silent while the page is still there and an error
   * thrown at nobody once it is not. Held in a ref rather than a flag inside
   * the effect because Retry runs the same load from outside it.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get<EquipmentInventoryResponse>(
        `${config.devicesApiUrl}/devices/inventory`,
        {
          // A device with no install date is only in `all`, so the section
          // would silently show nothing for it under any other cohort.
          params: { cohort: 'all', 'f.device_id': deviceId, limit: 100 },
        },
      );
      if (!alive.current) return;
      setRows(resp.data.rows);
    } catch (err: any) {
      if (!alive.current) return;
      const status = err?.response?.status;
      setError(status === 401 || status === 403 ? 'forbidden' : 'failed');
      setRows([]);
    } finally {
      if (alive.current) setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <section data-testid="equipment-inventory-section">
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Equipment inventory</h3>
        <Link
          to={`/equipment-inventory?device_id=${deviceId}`}
          className="text-sm text-link hover:underline"
        >
          Open in Equipment Inventory
        </Link>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading the equipment inventory…</p>}

      {!loading && error === 'forbidden' && (
        <p className="text-sm text-status-error-foreground">
          You do not have permission to view the equipment inventory
        </p>
      )}

      {!loading && error === 'failed' && (
        <p className="text-sm text-status-error-foreground">
          The equipment inventory could not be loaded.{' '}
          <Button
            variant="secondary"
            size="sm"
            onClick={load}
            className="ml-1"
            title="Ask the server for this device's equipment inventory again."
          >
            Retry
          </Button>
        </p>
      )}

      {!loading && !error && rows.length === 0 && (
        <p className="text-sm text-muted-foreground">No equipment inventory rows for this device.</p>
      )}

      {!loading && !error && rows.length > 0 && (
        <ul className="text-sm divide-y divide-gray-100">
          {rows.map((r) => (
            <InventoryRowItem key={r.field} row={r} />
          ))}
        </ul>
      )}
    </section>
  );
};

export default EquipmentInventorySection;
